import React, { useState, useEffect, useMemo } from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';


type AspectRatio = {
    name: string
    value: number
}
export const aspectRatios = [
  { name: '1:2', value: 1 / 2 },
  { name: '9:16', value: 9 / 16 },
  { name: '3:4', value: 3 / 4 },
  { name: '4:5', value: 4 / 5 },
  { name: '1:1', value: 1 },
  { name: '5:4', value: 5 / 4 },
  { name: '4:3', value: 4 / 3 },
  { name: '16:9', value: 16 / 9 },
  { name: '2:1', value: 2 },
];

export default function ImageSizePreview({
  currentRatio,
  setCurrentRatio,
}: {
  currentRatio: AspectRatio;
  setCurrentRatio: (ratio: AspectRatio) => void;
}) {
  const getSliderPosition = (ratio: AspectRatio) => {
    const index = aspectRatios.findIndex(r => r.value === ratio.value);
    if (index === -1) return 50; // Default to middle if not found
    return 10 + (index * (90 / (aspectRatios.length - 1)));
  };

  const [customRatio, setCustomRatio] = useState({ width: 1, height: 1 });
  const [sliderValue, setSliderValue] = useState(() => getSliderPosition(currentRatio));
  const [isCustomRatio, setIsCustomRatio] = useState(false);

  useEffect(() => {
    if (!isCustomRatio) {
      setSliderValue(getSliderPosition(currentRatio));
    }
  }, [currentRatio, isCustomRatio]);

  const actualRatio = useMemo(() => {
    return isCustomRatio
      ? customRatio.width / customRatio.height
      : currentRatio.value;
  }, [isCustomRatio, customRatio, currentRatio]);

  const handleSliderChange = (value: number[]) => {
    setSliderValue(value[0]);
    const index = Math.round(
      (value[0] - 10) / (90 / (aspectRatios.length - 1))
    );
    setCurrentRatio(aspectRatios[index]);
    setIsCustomRatio(false);
  };

  const resetImageSize = () => {
    setCurrentRatio(aspectRatios[4]); // Reset to 1:1
    setCustomRatio({ width: 1, height: 1 });
    setSliderValue(50);
    setIsCustomRatio(false);
  };

  const setOrientationRatio = (orientation: string) => {
    let ratio;
    let sliderPos;
    switch (orientation) {
      case 'Portrait':
        ratio = aspectRatios[2]; // 3:4
        sliderPos = 30;
        break;
      case 'Square':
        ratio = aspectRatios[4]; // 1:1
        sliderPos = 50;
        break;
      case 'Landscape':
        ratio = aspectRatios[6]; // 4:3
        sliderPos = 70;
        break;
      default:
        ratio = aspectRatios[4];
        sliderPos = 50;
    }
    setCurrentRatio(ratio);
    setSliderValue(sliderPos);
    setIsCustomRatio(false);
  };

  const handleCustomRatioChange = (
    dimension: 'width' | 'height',
    value: number
  ) => {
    setCustomRatio((prev) => ({ ...prev, [dimension]: value }));
    setIsCustomRatio(true);
  };

  useEffect(() => {
    if (!isCustomRatio) {
      const [width, height] = currentRatio.name.split(':').map(Number);
      setCustomRatio({ width, height });
    }
  }, [currentRatio, isCustomRatio]);

  const width = Math.round(1000 * Math.sqrt(actualRatio));
  const height = Math.round(1000 / Math.sqrt(actualRatio));

  const previewStyle =
    actualRatio >= 1
      ? { width: '100%', height: `${(1 / actualRatio) * 100}%` }
      : { width: `${actualRatio * 100}%`, height: '100%' };

  return (
    <div className="w-full rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-lg font-semibold dark:text-gray-200">Image Size</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetImageSize}
          className="flex items-center text-brand hover:text-brand-dark transition-colors duration-200"
        >
          <RefreshCw className="mr-1 h-4 w-4" />
          Reset
        </Button>
      </div>

      <div className="mb-2 flex flex-col xl:flex-row gap-4 xl:gap-0 items-center space-x-4">
        <div className="relative flex h-28 w-28 items-center justify-center border-2 border-gray-300 dark:border-gray-600 rounded-lg">
          <div className="absolute flex h-full w-full items-center justify-center overflow-hidden">
            <button
              className="absolute rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 transition-colors hover:border-brand"
              style={previewStyle}
            ></button>
          </div>
          <div
            className="absolute flex items-center justify-center gap-1 rounded-lg border-2 border-brand bg-gray-100 dark:bg-gray-700"
            style={previewStyle}
          >
            <input
              className="w-8 bg-transparent text-right dark:text-gray-200"
              value={customRatio.width}
              disabled
              onChange={(e) =>
                handleCustomRatioChange('width', parseInt(e.target.value) || 1)
              }
            />
            <span className="dark:text-gray-200">:</span>
            <input
              className="w-8 bg-transparent dark:text-gray-200"
              disabled
              value={customRatio.height}
              onChange={(e) =>
                handleCustomRatioChange('height', parseInt(e.target.value) || 1)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="mb-8 xl:mb-2 flex justify-between">
            {['Portrait', 'Square', 'Landscape'].map((orientation) => (
              <Button
                key={orientation}
                variant={
                  actualRatio === 1
                    ? orientation === 'Square'
                      ? 'default'
                      : 'outline'
                    : actualRatio < 1
                    ? orientation === 'Portrait'
                      ? 'default'
                      : 'outline'
                    : orientation === 'Landscape'
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                onClick={() => setOrientationRatio(orientation)}
                className={cn(
                  'transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700',
                  actualRatio === 1
                    ? orientation === 'Square'
                      ? 'bg-brand text-white border-2 border-brand'
                      : 'bg-white text-black dark:bg-white dark:text-black hover:bg-gray-100 dark:hover:bg-gray-100 dark:hover:text-brand'
                    : actualRatio < 1
                    ? orientation === 'Portrait'
                      ? 'bg-brand text-white border-2 border-brand'
                      : 'bg-white text-black dark:bg-white dark:text-black hover:bg-gray-100 dark:hover:bg-gray-100 dark:hover:text-brand'
                    : orientation === 'Landscape'
                    ? 'bg-brand text-white border-2 border-brand'
                    : 'bg-white text-black dark:bg-white dark:text-black hover:bg-gray-100 dark:hover:bg-gray-100 dark:hover:text-brand'
                )}
              >
                {orientation}
              </Button>
            ))}
          </div>
          <Slider
            min={10}
            max={100}
            step={1}
            value={[sliderValue]}
            onValueChange={handleSliderChange}
            className="w-full"
          />
        </div>
      </div>
      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
        {width} x {height}
      </div>
    </div>
  );
}
